/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { loadEntryPoint, type EntryPoint, type Environment } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { FE_UNATTRIBUTED } from '@jira/platform__observability-capabilities/src/constants.tsx';
import type { TenantContext } from '@jira/platform__tenant-context-types/src/tenant-context.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { Plugin, Route, RouterContext } from '@atlassian/react-resource-router';
import { withPreloadCapability } from '../../common/withPreloadCapability.tsx';
import { isExpired, getExpiresAt, isSameRouteMatch } from '../../common/utils/index.tsx';
import { getEntryPointsStore, getPrefetchingRoute, getLastLoadedRoute } from '../store/index.tsx';
import { waitForEntryPoint } from '../wait-for-entry-point/index.tsx';
import { type EntrypointStatEntry, getEntryPointStats } from '../get-entry-point-stats/index.tsx';
import loadEntryPointDataThenModules from '../load-entrypoint-data-then-modules/loadEntryPointDataThenModules.tsx';

interface EntryPointsPlugin extends Plugin {
	waitForEntryPoint: () => Promise<unknown>;
	getStats: () => Promise<ReadonlyArray<EntrypointStatEntry>>;
}

type EntryPointParams = {
	tenantContext: TenantContext;
};

export const createEntryPointsPlugin = ({
	environment,
	entryPointParams,
	noRootQueries = false,
}: {
	environment: Environment;
	entryPointParams: EntryPointParams;
	noRootQueries?: boolean;
}): EntryPointsPlugin => ({
	id: 'entry-points-plugin',
	routeLoad: ({ context }) => {
		const entryPoint = context.route.entryPoint?.();
		const prefetchingRoute = getPrefetchingRoute();

		let entryPointReference = null;

		if (entryPoint) {
			// check if route EntryPoint is already prefetched right before being loaded
			if (
				prefetchingRoute &&
				isSameRouteMatch({
					prevContextMatch: prefetchingRoute.routerContext.match,
					nextContextMatch: context.match,
				}) &&
				!isExpired(prefetchingRoute.expiresAt)
			) {
				entryPointReference = prefetchingRoute.entryPointReference;
			} else {
				const entryPointParamsWithContext: EntryPointRouteParams = {
					context,
					...entryPointParams,
				};
				if (__SERVER__ && noRootQueries) {
					// we can reassign here because SSR has a fresh context for every request
					const previousGetPreloadProps = entryPoint.getPreloadProps;
					const getPreloadProps: typeof entryPoint.getPreloadProps = (params) => {
						const preloadProps = previousGetPreloadProps(params);
						// @ts-expect-error The operand of a 'delete' operator cannot be a read-only property.ts(2704)
						delete preloadProps.queries;
						return preloadProps;
					};
					// @ts-expect-error Cannot assign to 'getPreloadProps' because it is a read-only property.ts(2540)
					entryPoint.getPreloadProps = getPreloadProps;
				}
				const loadEntryPointsWithContext = () =>
					loadEntryPoint(
						{ getEnvironment: () => environment },
						// oxlint-disable-next-line typescript/consistent-type-assertions, @typescript-eslint/no-explicit-any
						entryPoint as EntryPoint<any, typeof entryPointParamsWithContext>,
						entryPointParamsWithContext,
					);
				entryPointReference = __SERVER__
					? loadEntryPointDataThenModules(
							entryPoint,
							entryPointParamsWithContext,
							loadEntryPointsWithContext,
						)
					: loadEntryPointsWithContext();
			}
		}

		// saving entryPointReference helps to prevent cases when same EntryPoint is fetched multiple times
		// important when fetchPolicy=network-only is used
		getEntryPointsStore().actions.setLastLoadedRoute({
			route: context.route,
			entryPointReference,
		});

		return {
			entryPoint: entryPointReference,
		};
	},
	routePrefetch: ({ nextContext }) => {
		const entryPoint = nextContext.route.entryPoint?.();

		if (entryPoint) {
			const prevPrefetchingRoute = getPrefetchingRoute();

			// check if EntryPoint is already prefetched and not expired
			if (
				prevPrefetchingRoute &&
				isSameRouteMatch({
					prevContextMatch: prevPrefetchingRoute.routerContext.match,
					nextContextMatch: nextContext.match,
				}) &&
				!isExpired(prevPrefetchingRoute.expiresAt)
			) {
				return;
			}

			const entryPointToPrefetch = expVal(
				'jpo-42550-improve-capability-tracking',
				'isEnabled',
				false,
			)
				? withPreloadCapability(entryPoint, nextContext.route.meta?.capability ?? FE_UNATTRIBUTED)
				: entryPoint;

			const entryPointReference = loadEntryPoint(
				{ getEnvironment: () => environment },
				// oxlint-disable-next-line typescript/consistent-type-assertions
				entryPointToPrefetch as EntryPoint<
					// oxlint-disable-next-line @typescript-eslint/no-explicit-any
					any,
					{ tenantContext: TenantContext; context: RouterContext<Route> }
				>,
				{
					context: nextContext,
					...entryPointParams,
				},
			);

			getEntryPointsStore().actions.setPrefetchingRoute({
				routerContext: nextContext,
				expiresAt: getExpiresAt(),
				entryPointReference,
			});
		} else {
			getEntryPointsStore().actions.setPrefetchingRoute({
				routerContext: nextContext,
				expiresAt: 0,
				entryPointReference: null,
			});
		}
	},
	waitForEntryPoint: () => {
		const lastLoadedRoute = getLastLoadedRoute();
		const entryPointReference = lastLoadedRoute?.entryPointReference ?? null;

		if (entryPointReference) {
			return waitForEntryPoint(entryPointReference);
		}

		return Promise.resolve();
	},
	getStats: async () => {
		const lastLoadedRoute = getLastLoadedRoute();
		const entryPointReference = lastLoadedRoute?.entryPointReference ?? null;

		if (entryPointReference) {
			return getEntryPointStats(entryPointReference);
		}

		return [];
	},
});
